// src/pages/AudioLanguage/ListAudioLanguage.js
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { useParams} from 'react-router-dom';

import DataTable from 'react-data-table-component';
import backendURL from "../../config";
import AddLanguages from "./AddLanguages";
import Drawer from "@mui/material/Drawer";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Box, Button as MuiButton, CircularProgress } from '@mui/material';

const ListAudioLanguage = forwardRef(({ isSetupMode = false , chatbotId, onSetupComplete, onSetupError }, ref) => {
  
  
  
  
  const { chatbotId: paramsChatbotId } = useParams();
  let chatBotId = chatbotId ? chatbotId : paramsChatbotId
  console.log(chatBotId)
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);

  const token = localStorage.getItem("access_token");

  const handleReload = () => setReload(prev => !prev);

  useEffect(() => {
    if (!isSetupMode) {
      document.title = "Audio & Language Settings | KlonIT Web Application";
    }

    if (chatBotId || !isSetupMode) {
        fetchDocuments(chatBotId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, isSetupMode]); // Added isSetupMode here for completeness, though it rarely changes post-mount

  const fetchDocuments = useCallback(async (currentChatbotId) => {
    setLoading(true);
    setError(null);
    if (!token) {
      const noTokenMsg = "Authentication token missing.";
      setLoading(false);
      if (isSetupMode && onSetupError) onSetupError(noTokenMsg); else setError(noTokenMsg);
      setDocuments([]); setFilteredDocuments([]);
      return;
    }
    try {

      const params = currentChatbotId ? { chatbot_id: currentChatbotId } : {};

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${backendURL}/get-language-audio`, { headers, params });
      let responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      responseData = Array.isArray(responseData) ? responseData : [];
      setDocuments(responseData);
      setFilteredDocuments(responseData);

      if (isSetupMode && responseData.length > 0) { // Update localStorage only if languages are set in setup mode
        try {
          let userData = {};
          const userStr = localStorage.getItem('user');
          if (userStr) userData = JSON.parse(userStr);
          userData.audio_languages = responseData;
          if (!userData.chatbot_setup_configuration) userData.chatbot_setup_configuration = {};
          userData.chatbot_setup_configuration.is_language_set = true; // Mark as set
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          if (onSetupError) onSetupError("Failed to update local session with audio languages.");
        }
      } else if (isSetupMode && responseData.length === 0) {
        // If in setup mode and no languages are set (e.g., after deleting all), clear the flag
         try {
            let userData = {};
            const userStr = localStorage.getItem('user');
            if (userStr) userData = JSON.parse(userStr);
            if (userData.chatbot_setup_configuration) { // Only modify if it exists
                userData.chatbot_setup_configuration.is_language_set = false;
            }
            // No need to remove audio_languages array, just set its flag to false.
            // Or, userData.audio_languages = [];
            localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
            // Handle error if necessary
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error fetching audio configurations.";
      if (isSetupMode && onSetupError) onSetupError(errorMsg); else setError(errorMsg);
      setDocuments([]); setFilteredDocuments([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSetupMode]); // Removed onSetupError, onSetupComplete from deps

  const handleDelete = async (documentId) => {
    if (!token) { setError("Authentication token missing."); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${backendURL}/delete-language-audio/${documentId}`, { headers });
      setError("Language deleted successfully.");
      fetchDocuments(chatBotId); // Reload data
    } catch (err) {
      setError(err.response?.data?.message || "Error deleting language.");
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredDocuments(
      documents.filter(doc =>
        (doc.website_lang || "").toLowerCase().includes(value) ||
        (doc.website_gender || "").toLowerCase().includes(value) ||
        (doc.created_date || "").toLowerCase().includes(value)
      )
    );
  };

  const columns = [
    { name: "Language", selector: (row) => row.website_lang || "N/A", sortable: true, wrap: true },
    { name: "Gender", selector: (row) => row.website_gender || "N/A", sortable: true },
    { name: "Default", selector: (row) => (row.default_language ? "Yes" : "No"), sortable: true },
    { name: "Created", selector: (row) => row.created_date ? new Date(row.created_date).toLocaleDateString() : "N/A", sortable: true },
    {
      name: "Action",
      cell: (row) => (
        <IconButton onClick={() => handleDelete(row.id)} size="small" color="error">
          <i className="fa fa-trash-o"></i> {/* Use MUI Icon for consistency if preferred: <DeleteIcon /> */}
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const toggleDrawer = (open) => setIsDrawerOpen(open);

  useImperativeHandle(ref, () => ({
    checkStepCompletion: () => {
      if (documents.length === 0) {
        const noLangMsg = "Please add at least one audio language.";
        if (isSetupMode && onSetupError) onSetupError(noLangMsg); else setError(noLangMsg);
        return false;
      }
      if (isSetupMode && onSetupComplete) onSetupComplete({ languagesAdded: documents.length });
      return true;
    }
  }));


  // --- Core UI for adding and listing languages ---
  const LanguageManagementUI = (
    <>
      <div className={`row align-items-center ${isSetupMode ? "mb-3" : "card-header"}`}>
        <div className={isSetupMode ? "col-12 text-center" : "col-lg-8"}>
          <MuiButton variant="contained" color="primary" onClick={() => toggleDrawer(true)} startIcon={<i className="fa fa-plus" />}>
            Add New Language
          </MuiButton>
        </div>
        {!isSetupMode && (
          <div className="col-lg-4">
            <input
              type="text"
              placeholder="Search languages..."
              className="form-control" // Standard Bootstrap class
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        )}
      </div>

      <div className={isSetupMode ? "mt-2" : "card-body"}> {/* card-body for non-setup, margin-top for setup */}
        {loading ? (
          <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /><Typography sx={{ mt: 1 }}>Loading languages...</Typography></Box>
        ) : filteredDocuments.length === 0 ? (
          <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary', p: 2 }}>
            No audio languages configured yet. Click "Add New Language" to begin.
          </Typography>
        ) : (
          <DataTable
            columns={columns}
            data={filteredDocuments}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            dense={isSetupMode}
            noHeader={isSetupMode} // Hide header in setup mode for cleaner look
            paginationComponentOptions={{ noRowsPerPage: isSetupMode }} // Simpler pagination for setup
          />
        )}
        {error && !isSetupMode && ( // Only show internal error if not in setup mode (setup errors go to parent)
          <div className="alert alert-warning mt-2">{error}</div>
        )}
         {isSetupMode && error && ( // For setup mode, display error passed from parent or internal via onSetupError
            <Typography color="error" sx={{ textAlign: 'center', mt: 1 }}>{error}</Typography>
        )}
      </div>
    </>
  );
  // --- End Core UI ---


  if (isSetupMode) {
    return (
      <Box sx={{ p: {xs: 1, sm: 2} }}> {/* Padding for embedded mode */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6">Configure Audio Languages</Typography>
          <Typography variant="body2" color="textSecondary">
            Add one or more languages and voices for your chatbot's audio responses.
            At least one language is required.
          </Typography>
        </Box>
        {LanguageManagementUI}
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => toggleDrawer(false)}
            PaperProps={{ sx: { width: {xs: '95%', sm: '80%', md: '500px'}, maxWidth: '500px' } }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton onClick={() => toggleDrawer(false)}><CloseIcon /></IconButton>
            </Box>
            <AddLanguages chatbotId={chatBotId} onClose={() => toggleDrawer(false)} setReload={handleReload} />
        </Drawer>
      </Box>
    );
  }

  // --- Fallback to original non-setup mode structure ---
  return (
    <div> {/* Original outermost div */}
      <main className="content">
        <div className="container-fluid p-0">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle"> <strong>Audio & Language</strong> Setting</h1>
          </div>
          <div className="row">
            <div className="col-12 col-lg-12 col-xxl-12 d-flex">
              <div className="card flex-fill">
                {/* Re-integrate LanguageManagementUI here for non-setup mode */}
                {LanguageManagementUI}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => toggleDrawer(false)}
        PaperProps={{ style: { width: "500px" } }} // Original width for non-setup
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
          <IconButton onClick={() => toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <AddLanguages chatbotId={chatBotId} onClose={() => toggleDrawer(false)} setReload={handleReload} />
      </Drawer>
    </div>
  );
});

export default ListAudioLanguage;